import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle, Check, Loader2, Package as PackageIcon, Plus, RefreshCw, Search, Send, X,
} from 'lucide-react';
import {
  cancelSupplyRequest,
  createSupplyProduct,
  getSupplyRequests,
  getSupplyStock,
  receiveSupplyRequest,
  requestSupply,
  sendSupplyRequest,
  setSupplyStock,
} from '../../services/supplyService';

// ─── One page, two audiences ────────────────────────────────────────────────
//
// Store view:   own count + what central has spare, and a Request button.
//               There is deliberately no way to type a new quantity — a store's
//               stock only rises by receiving a transfer.
// Central view: the same table plus editable quantities and the request queue,
//               because central is the only party allowed to restock.
//
// Which one you get is decided by the server (`canRestock` / `isCentral` in the
// /api/supplies/stock response), not by anything the client asserts.

const Wrap = styled.div`display: flex; flex-direction: column; gap: 1rem;`;

const Bar = styled.div`
  display: flex; align-items: center; gap: 0.6rem; flex-wrap: wrap;
`;

const SearchWrap = styled.div`
  display: flex; align-items: center; gap: 0.4rem; flex: 1; min-width: 200px;
  background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 0.45rem 0.7rem;
`;

const SearchInput = styled.input`
  border: none; outline: none; flex: 1; font-size: 0.875rem; font-family: inherit; color: #0f172a;
  background: transparent;
`;

const Btn = styled.button`
  display: inline-flex; align-items: center; gap: 0.35rem;
  background: ${p => p.$primary ? '#10b981' : '#fff'};
  color: ${p => p.$primary ? '#fff' : '#475569'};
  border: 1px solid ${p => p.$primary ? '#10b981' : '#e2e8f0'};
  border-radius: 10px; padding: 0.45rem 0.75rem; font-size: 0.8125rem; font-weight: 600;
  font-family: inherit; cursor: pointer; transition: all 0.15s;
  &:hover:not(:disabled) { border-color: #10b981; color: ${p => p.$primary ? '#fff' : '#10b981'}; }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

const Card = styled.div`
  background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; overflow: hidden;
`;

const Table = styled.table`
  width: 100%; border-collapse: collapse;
  th, td { padding: 0.6rem 0.85rem; text-align: left; font-size: 0.8125rem; border-bottom: 1px solid #f1f5f9; }
  th { background: #f8fafc; color: #64748b; font-size: 0.6875rem; text-transform: uppercase;
       letter-spacing: 0.05em; font-weight: 700; }
  tbody tr:last-child td { border-bottom: none; }
  td.num { font-variant-numeric: tabular-nums; font-weight: 700; color: #0f172a; }
`;

const Pill = styled.span`
  display: inline-flex; align-items: center; gap: 0.25rem;
  background: ${p => p.$tone === 'low' ? '#fef2f2' : p.$tone === 'ok' ? '#f0fdf4' : '#f1f5f9'};
  color: ${p => p.$tone === 'low' ? '#b91c1c' : p.$tone === 'ok' ? '#15803d' : '#475569'};
  border-radius: 999px; padding: 0.15rem 0.5rem; font-size: 0.6875rem; font-weight: 700;
`;

const QtyInput = styled.input`
  width: 68px; padding: 0.3rem 0.45rem; border: 1px solid #e2e8f0; border-radius: 8px;
  font-size: 0.8125rem; font-family: inherit; text-align: right; font-variant-numeric: tabular-nums;
  &:focus { outline: none; border-color: #10b981; }
`;

const NOTICE_TONES = {
  error:   { bg: '#fee2e2', fg: '#991b1b', border: '#fecaca' },
  success: { bg: '#dcfce7', fg: '#166534', border: '#bbf7d0' },
  // Not a failure the user caused — the feature just isn't switched on yet.
  info:    { bg: '#eff6ff', fg: '#1e40af', border: '#bfdbfe' },
};

const Notice = styled.div`
  background: ${p => (NOTICE_TONES[p.$kind] || NOTICE_TONES.error).bg};
  color: ${p => (NOTICE_TONES[p.$kind] || NOTICE_TONES.error).fg};
  border: 1px solid ${p => (NOTICE_TONES[p.$kind] || NOTICE_TONES.error).border};
  border-radius: 10px; padding: 0.55rem 0.8rem; font-size: 0.85rem; line-height: 1.5;
`;

const Empty = styled.div`
  padding: 2.5rem 1rem; text-align: center; color: #94a3b8; font-size: 0.875rem;
  display: flex; flex-direction: column; align-items: center; gap: 0.5rem;
`;

const Backdrop = styled.div`
  position: fixed; inset: 0; background: rgba(15, 23, 42, 0.45); display: flex;
  align-items: center; justify-content: center; z-index: 1000; padding: 1rem;
`;

const Modal = styled.div`
  background: #fff; border-radius: 16px; width: 100%; max-width: 420px; padding: 1.25rem;
  display: flex; flex-direction: column; gap: 0.75rem;
`;

const Field = styled.label`
  display: flex; flex-direction: column; gap: 0.25rem; font-size: 0.75rem; font-weight: 700;
  color: #475569; text-transform: uppercase; letter-spacing: 0.04em;
  input, select {
    padding: 0.5rem 0.6rem; border: 1px solid #e2e8f0; border-radius: 9px; font-size: 0.875rem;
    font-family: inherit; font-weight: 500; text-transform: none; letter-spacing: normal; color: #0f172a;
    &:focus { outline: none; border-color: #10b981; }
  }
`;

const CATEGORY_COPY = {
  accessory: {
    title: 'Accessories',
    blurb: 'Sold to customers — cases, cloths, chains. Request what you need from central; stock lands here when the delivery is received.',
  },
  consumable: {
    title: 'Consumables',
    blurb: 'Internal supplies — screws, cleaning solution, bags. Request what you need from central; stock lands here when the delivery is received.',
  },
};

// refreshKey lets a parent screen's Refresh button reach this page's own data.
// Embedded in the central dashboard it fetched only on mount, so pressing
// Refresh on those tabs did nothing at all.
const SupplyStockPage = ({ category = 'accessory', refreshKey = 0 }) => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ canRestock: false, isCentral: false });
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [notice, setNotice] = useState(null);
  const [search, setSearch] = useState('');
  const [drafts, setDrafts] = useState({});
  const [setupRequired, setSetupRequired] = useState(false);
  const [askFor, setAskFor] = useState(null);
  const [askQty, setAskQty] = useState(1);
  const [showNewProduct, setShowNewProduct] = useState(false);
  const [newProduct, setNewProduct] = useState({ name: '', sku: '', brand: '', unit: 'piece', price: '', minQty: 0 });

  const copy = CATEGORY_COPY[category] || CATEGORY_COPY.accessory;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [stock, reqs] = await Promise.all([
        getSupplyStock({ category }),
        getSupplyRequests({ category }).catch(() => ({ requests: [] })),
      ]);
      setItems(stock.items || []);
      setMeta({ canRestock: Boolean(stock.canRestock), isCentral: Boolean(stock.isCentral) });
      setRequests((reqs.requests || []).filter(r => r.category === category));
      setSetupRequired(false);
      setNotice(null);
    } catch (err) {
      // The migration not being run yet isn't an error the user caused, and the
      // raw PostgREST text ("schema cache") means nothing to a store manager.
      if (err.response?.data?.setupRequired) {
        setSetupRequired(true);
        setItems([]);
        setRequests([]);
        setNotice({ kind: 'info', message: err.response.data.message });
      } else {
        setNotice({ kind: 'error', message: err.response?.data?.message || 'Failed to load stock.' });
      }
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(({ product }) =>
      [product.name, product.sku, product.brand].some(v => String(v || '').toLowerCase().includes(q))
    );
  }, [items, search]);

  // Requests this store is still waiting on, keyed by product so each row can
  // show "12 on the way" instead of the manager re-requesting the same thing.
  const pendingByProduct = useMemo(() => {
    const map = new Map();
    for (const r of requests) {
      if (['received', 'cancelled', 'declined'].includes(r.status)) continue;
      map.set(r.supplyProductId, (map.get(r.supplyProductId) || 0) + (Number(r.qty) || 0));
    }
    return map;
  }, [requests]);

  const incoming = useMemo(
    () => requests.filter(r => !['received', 'cancelled', 'declined'].includes(r.status)),
    [requests]
  );

  const saveQty = async (productId) => {
    const raw = drafts[productId];
    if (raw === undefined || raw === '') return;
    setBusyId(productId);
    setNotice(null);
    try {
      await setSupplyStock({ supplyProductId: productId, qty: Number(raw), reason: 'central restock' });
      setDrafts(d => { const next = { ...d }; delete next[productId]; return next; });
      await load();
      setNotice({ kind: 'success', message: 'Central stock updated.' });
    } catch (err) {
      setNotice({ kind: 'error', message: err.response?.data?.message || 'Failed to update stock.' });
    } finally {
      setBusyId(null);
    }
  };

  const submitRequest = async () => {
    if (!askFor) return;
    setBusyId(askFor.product._id);
    setNotice(null);
    try {
      const { request } = await requestSupply({ supplyProductId: askFor.product._id, qty: Number(askQty) || 1 });
      setAskFor(null);
      setAskQty(1);
      await load();
      setNotice({
        kind: 'success',
        message: request?.status === 'supplier_order_required'
          ? 'Requested. Central does not have it in stock and will order it from the supplier.'
          : 'Requested. Central has reserved it and will send it with the next package.',
      });
    } catch (err) {
      setNotice({ kind: 'error', message: err.response?.data?.message || 'Failed to send request.' });
    } finally {
      setBusyId(null);
    }
  };

  const markSent = async (request) => {
    setBusyId(request._id);
    setNotice(null);
    try {
      await sendSupplyRequest(request._id);
      await load();
      setNotice({
        kind: 'success',
        message: `${request.qty}× ${request.productName} marked as sent. ${request.requestingStore?.name || 'The store'} confirms it on arrival.`,
      });
    } catch (err) {
      setNotice({ kind: 'error', message: err.response?.data?.message || 'Failed to mark as sent.' });
    } finally {
      setBusyId(null);
    }
  };

  const confirmReceive = async (request) => {
    setBusyId(request._id);
    setNotice(null);
    try {
      await receiveSupplyRequest(request._id);
      await load();
      setNotice({ kind: 'success', message: `Received ${request.qty}× ${request.productName}.` });
    } catch (err) {
      setNotice({ kind: 'error', message: err.response?.data?.message || 'Failed to confirm receipt.' });
    } finally {
      setBusyId(null);
    }
  };

  const cancel = async (request) => {
    setBusyId(request._id);
    setNotice(null);
    try {
      await cancelSupplyRequest(request._id);
      await load();
      setNotice({ kind: 'success', message: 'Request cancelled.' });
    } catch (err) {
      setNotice({ kind: 'error', message: err.response?.data?.message || 'Failed to cancel.' });
    } finally {
      setBusyId(null);
    }
  };

  const addProduct = async () => {
    if (!newProduct.name.trim()) return;
    setBusyId('new');
    setNotice(null);
    try {
      await createSupplyProduct({ ...newProduct, category });
      setShowNewProduct(false);
      setNewProduct({ name: '', sku: '', brand: '', unit: 'piece', price: '', minQty: 0 });
      await load();
      setNotice({ kind: 'success', message: 'Item added to the catalog.' });
    } catch (err) {
      setNotice({ kind: 'error', message: err.response?.data?.message || 'Failed to add item.' });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Wrap>
      <p style={{ margin: 0, fontSize: '0.8125rem', color: '#64748b', lineHeight: 1.5 }}>{copy.blurb}</p>

      {notice && <Notice $kind={notice.kind}>{notice.message}</Notice>}

      {/* Nothing below this point can work until the tables exist, and an empty
          search box over an empty table just looks broken. */}
      {setupRequired ? (
        <Bar>
          <Btn type="button" onClick={load} disabled={loading}>
            <RefreshCw size={13} /> {t('supplyStock.tryAgain')}
          </Btn>
        </Bar>
      ) : (
      <>
      <Bar>
        <SearchWrap>
          <Search size={14} color="#94a3b8" />
          <SearchInput
            placeholder={`Search ${copy.title.toLowerCase()}…`}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && <X size={14} color="#94a3b8" style={{ cursor: 'pointer' }} onClick={() => setSearch('')} />}
        </SearchWrap>
        <Btn type="button" onClick={load} disabled={loading}>
          <RefreshCw size={13} /> {t('supplyStock.refresh')}
        </Btn>
        {meta.canRestock && (
          <Btn type="button" $primary onClick={() => setShowNewProduct(true)}>
            <Plus size={13} /> {t('supplyStock.newItem')}
          </Btn>
        )}
      </Bar>

      {/* Deliveries the store is waiting on. Confirming receipt is the only way
          store stock goes up, so it lives above the table where it's obvious. */}
      {!meta.isCentral && incoming.length > 0 && (
        <Card>
          <Table>
            <thead>
              <tr>
                <th>{t('supplyStock.onTheWayFromCentral')}</th>
                <th>{t('supplyStock.qty')}</th>
                <th>{t('supplyStock.status')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {incoming.map(r => (
                <tr key={r._id}>
                  <td>{r.productName}</td>
                  <td className="num">{r.qty}</td>
                  <td>
                    <Pill $tone={r.sentAt ? 'ok' : r.status === 'supplier_order_required' ? 'low' : undefined}>
                      {r.sentAt ? t('supplyStock.sentOnItsWay')
                        : r.status === 'supplier_order_required' ? t('supplyStock.waitingOnSupplier')
                          : t('supplyStock.reservedAtCentral')}
                    </Pill>
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <Btn type="button" $primary disabled={busyId === r._id} onClick={() => confirmReceive(r)}>
                      {busyId === r._id ? <Loader2 size={13} /> : <Check size={13} />} {t('supplyStock.received')}
                    </Btn>
                    <Btn type="button" disabled={busyId === r._id} onClick={() => cancel(r)} style={{ marginLeft: 6 }}>
                      {t('supplyStock.cancel')}
                    </Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      <Card>
        <Table>
          <thead>
            <tr>
              <th>{t('supplyStock.item')}</th>
              <th>{t('supplyStock.unit')}</th>
              {category === 'accessory' && <th>{t('supplyStock.price')}</th>}
              <th>{meta.isCentral ? t('supplyStock.centralStock') : t('supplyStock.inThisStore')}</th>
              {!meta.isCentral && <th>{t('supplyStock.availableAtCentral')}</th>}
              <th />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6}><Empty><Loader2 size={22} />{t('supplyStock.loading')}</Empty></td></tr>
            ) : visible.length === 0 ? (
              <tr>
                <td colSpan={6}>
                  <Empty>
                    <PackageIcon size={30} />
                    {items.length === 0
                      ? `No ${copy.title.toLowerCase()} in the catalog yet.${meta.canRestock ? ' Add the first one.' : ' Central has not added any yet.'}`
                      : t('supplyStock.nothingMatchesThatSearch')}
                  </Empty>
                </td>
              </tr>
            ) : visible.map(({ product, qty, isLow, centralAvailableQty, reservedQty }) => {
              const onTheWay = pendingByProduct.get(product._id) || 0;
              const draft = drafts[product._id];
              return (
                <tr key={product._id}>
                  <td>
                    <div style={{ fontWeight: 600, color: '#0f172a' }}>{product.name}</div>
                    <div style={{ fontSize: '0.6875rem', color: '#94a3b8' }}>
                      {[product.brand, product.sku].filter(Boolean).join(' · ') || '—'}
                    </div>
                  </td>
                  <td style={{ color: '#64748b' }}>{product.unit}</td>
                  {category === 'accessory' && (
                    <td className="num">{product.price != null ? `${Number(product.price).toLocaleString()} DZD` : '—'}</td>
                  )}
                  <td className="num">
                    {meta.canRestock && meta.isCentral ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <QtyInput
                          type="number"
                          min="0"
                          value={draft !== undefined ? draft : qty}
                          onChange={e => setDrafts(d => ({ ...d, [product._id]: e.target.value }))}
                        />
                        {draft !== undefined && String(draft) !== String(qty) && (
                          <Btn type="button" $primary disabled={busyId === product._id} onClick={() => saveQty(product._id)}>
                            <Check size={13} />
                          </Btn>
                        )}
                        {/* Reserved units are inside `qty` but already promised,
                            so central needs to see why the number it can send
                            is lower than the number on the shelf. */}
                        {reservedQty > 0 && <Pill>{reservedQty} reserved</Pill>}
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {qty}
                        {isLow && <Pill $tone="low"><AlertTriangle size={10} /> {t('supplyStock.low')}</Pill>}
                        {onTheWay > 0 && <Pill>+{onTheWay} coming</Pill>}
                      </div>
                    )}
                  </td>
                  {!meta.isCentral && (
                    <td className="num" style={{ color: centralAvailableQty > 0 ? '#15803d' : '#b91c1c' }}>
                      {centralAvailableQty}
                    </td>
                  )}
                  <td style={{ textAlign: 'right' }}>
                    {!meta.isCentral && (
                      <Btn
                        type="button"
                        onClick={() => { setAskFor({ product, centralAvailableQty }); setAskQty(1); }}
                      >
                        <Send size={13} /> {t('supplyStock.request')}
                      </Btn>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Card>

      {/* Central's queue of what stores have asked for. */}
      {meta.isCentral && requests.length > 0 && (
        <Card>
          <Table>
            <thead>
              <tr><th>{t('supplyStock.storeRequests')}</th><th>{t('supplyStock.store')}</th><th>{t('supplyStock.qty')}</th><th>{t('supplyStock.status')}</th><th /></tr>
            </thead>
            <tbody>
              {requests.slice(0, 30).map(r => {
                const open = !['received', 'cancelled', 'declined'].includes(r.status);
                const sent = Boolean(r.sentAt);
                return (
                  <tr key={r._id}>
                    <td>{r.productName}</td>
                    <td style={{ color: '#64748b' }}>{r.requestingStore?.name || '—'}</td>
                    <td className="num">{r.qty}</td>
                    <td>
                      <Pill $tone={r.status === 'received' ? 'ok' : sent ? 'ok' : r.status === 'supplier_order_required' ? 'low' : undefined}>
                        {r.status === 'received' ? 'received'
                          : sent ? t('supplyStock.sentAwaitingStore')
                            : String(r.status || '').replace(/_/g, ' ')}
                      </Pill>
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {open && !sent && (
                        <Btn type="button" $primary disabled={busyId === r._id} onClick={() => markSent(r)}>
                          <Send size={13} /> {t('supplyStock.send')}
                        </Btn>
                      )}
                      {open && (
                        <Btn type="button" disabled={busyId === r._id} onClick={() => cancel(r)} style={{ marginLeft: 6 }}>
                          {t('supplyStock.cancel')}
                        </Btn>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card>
      )}
      </>
      )}

      {askFor && (
        <Backdrop onClick={() => setAskFor(null)}>
          <Modal onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#0f172a' }}>
              {t('supplyStock.request')} {askFor.product.name}
            </div>
            <div style={{ fontSize: '0.8125rem', color: '#64748b' }}>
              {t('supplyStock.centralHas')} <strong>{askFor.centralAvailableQty}</strong> {t('supplyStock.available')}
              {askFor.centralAvailableQty === 0 && ' Your request will be queued for a supplier order.'}
            </div>
            <Field>
              {t('supplyStock.quantity')}
              <input
                type="number"
                min="1"
                value={askQty}
                onChange={e => setAskQty(e.target.value)}
                autoFocus
              />
            </Field>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Btn type="button" onClick={() => setAskFor(null)}>{t('supplyStock.cancel')}</Btn>
              <Btn type="button" $primary onClick={submitRequest} disabled={busyId === askFor.product._id}>
                <Send size={13} /> {t('supplyStock.sendRequest')}
              </Btn>
            </div>
          </Modal>
        </Backdrop>
      )}

      {showNewProduct && (
        <Backdrop onClick={() => setShowNewProduct(false)}>
          <Modal onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#0f172a' }}>
              {t('supplyStock.new')} {category === 'accessory' ? 'accessory' : 'consumable'}
            </div>
            <Field>{t('supplyStock.name')}<input value={newProduct.name} onChange={e => setNewProduct(p => ({ ...p, name: e.target.value }))} autoFocus /></Field>
            <div style={{ display: 'flex', gap: 8 }}>
              <Field style={{ flex: 1 }}>{t('supplyStock.brand')}<input value={newProduct.brand} onChange={e => setNewProduct(p => ({ ...p, brand: e.target.value }))} /></Field>
              <Field style={{ flex: 1 }}>{t('supplyStock.sku')}<input value={newProduct.sku} onChange={e => setNewProduct(p => ({ ...p, sku: e.target.value }))} /></Field>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Field style={{ flex: 1 }}>{t('supplyStock.unit')}<input value={newProduct.unit} onChange={e => setNewProduct(p => ({ ...p, unit: e.target.value }))} /></Field>
              {category === 'accessory' && (
                <Field style={{ flex: 1 }}>{t('supplyStock.priceDzd')}<input type="number" min="0" value={newProduct.price} onChange={e => setNewProduct(p => ({ ...p, price: e.target.value }))} /></Field>
              )}
              <Field style={{ flex: 1 }}>{t('supplyStock.lowAt')}<input type="number" min="0" value={newProduct.minQty} onChange={e => setNewProduct(p => ({ ...p, minQty: e.target.value }))} /></Field>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Btn type="button" onClick={() => setShowNewProduct(false)}>{t('supplyStock.cancel')}</Btn>
              <Btn type="button" $primary onClick={addProduct} disabled={busyId === 'new' || !newProduct.name.trim()}>
                <Plus size={13} /> {t('supplyStock.add')}
              </Btn>
            </div>
          </Modal>
        </Backdrop>
      )}
    </Wrap>
  );
};

export default SupplyStockPage;
